import {
    useAPIQuery,
    useGlobalAPIQueryContext,
} from "@dev2_techwalnut/tw-api-query-hook";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { z } from "zod";
import GeneratedBOMDetails from "./GeneratedBOMDetails";
import CheckBOM from "./CheckBOM";
import secureLocalStorage from "react-secure-storage";
import AllotFG from "./AllotFG";
import AllotWIP from "./AllotWIP";

const createSchema = (
    targetQuantity,
    targetQuantityTolerance,
    minTargetQuantityTolerance
) => {
    const target_quantity_tolerance =
        Number(targetQuantity) * minTargetQuantityTolerance;
    const total_quantity_tolerance =
        Number(targetQuantity) * targetQuantityTolerance;
    return z.object({
        work_orders: z
            .array(
                z.object({
                    work_order_id: z.number().optional().nullable(),
                    target_quantity: z
                        .number({
                            required_error: "Target quantity is required",
                            invalid_type_error: "Target quantity is required",
                        })
                        .min(
                            target_quantity_tolerance ?? 1,
                            `Quantity must be at least ${
                                target_quantity_tolerance ?? 1
                            }`
                        ),
                    target_quantity_actual: z
                        .number({
                            required_error: "Target quantity is required",
                            invalid_type_error: "Target quantity is required",
                        })
                        .min(
                            target_quantity_tolerance ?? 1,
                            `Quantity must be at least ${
                                target_quantity_tolerance ?? 1
                            }`
                        ),
                    prod_start_date: z.coerce
                        .date({
                            errorMap: ({ code }, { defaultError }) => {
                                if (code == "invalid_date")
                                    return {
                                        message:
                                            "Production start date is required.",
                                    };
                                return { message: defaultError };
                            },
                        })
                        .transform((date) => date.toISOString().split("T")[0]),
                    dispatch_date: z.coerce
                        .date({
                            errorMap: ({ code }, { defaultError }) => {
                                if (code == "invalid_date")
                                    return {
                                        message: "Dispatch date is required.",
                                    };
                                return { message: defaultError };
                            },
                        })
                        .transform((date) => date.toISOString().split("T")[0]),
                })
            )
            .min(1, "At least one work order is required")
            .superRefine((workOrders, ctx) => {
                const total = workOrders.reduce(
                    (sum, wo) => sum + (wo.target_quantity || 0),
                    0
                );

                if (total > total_quantity_tolerance) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Total quantity (${total}) cannot exceed target (${total_quantity_tolerance}).`,
                        path: ["target_quantity"],
                    });
                }
            }),
    });
};

const WorkOrderSplitUpdate = () => {
    const privileges = secureLocalStorage.getItem("privileges");
    const userID = secureLocalStorage.getItem("userID");
    const location = useLocation();
    const navigate = useNavigate();
    const { id: sales_order_id } = location.state || {};

    const [currentWIP, setCurrentWIP] = useState(null);
    const [recheckBom, setRecheckBom] = useState(false);

    const { invalidateApi } = useGlobalAPIQueryContext();

    const { useFetch: fetchGenericData } = useAPIQuery({
        key: "api/generic",
        endpoint: "/api/generic/",
    });

    const { data: genericData, isLoading: IsGenericLoading } = fetchGenericData(
        {
            abortOnNewRequest: true,
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 3,
            retry: 3,
            keepPlaceholder: true,
            refetchOnReconnect: true,
        }
    );

    const { useFetch: fetchSalesOrder } = useAPIQuery({
        key: `api/sales-order/${sales_order_id}`,
        endpoint: `/api/sales-order/${sales_order_id}/`,
    });

    const {
        data: salesOrderData,
        isLoading: isSalesOrderLoading,
        isError: isSalesOrderError,
    } = fetchSalesOrder({
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const salesOrder = salesOrderData?.results?.data;
    const targetQuantity = salesOrder?.to_be_produced;

    const rollbackRef = useRef(null);

    const {
        useFetch: fetchWorkOrder,
        useCreate: createWorkOrder,
        useDelete: deleteWorkOrder,
    } = useAPIQuery({
        key: "api/work-order",
        endpoint: "/api/work-order/",
    });

    const {
        data: workOrderData,
        isFetching: isWorkOrderFetching,
        dataUpdatedAt: workOrderDataUpdatedAt,
    } = fetchWorkOrder({
        params: {
            sales_order_id: salesOrder?.id,
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
        enabled: !!salesOrder,
    });

    const multiplicationFactor = useMemo(() => {
        return workOrderData?.results?.data?.[0]?.multiplication_factor || 1;
    });

    const { mutate: mutateCreateWorkOrder, isPending } = createWorkOrder({
        successMessageFromServer: true,
        errorMessageFromServer: true,
        onSuccess: () => {
            invalidateApi({ key: "api/work-order" });
        },
        onError: () => {
            if (rollbackRef.current) {
                replace_work_orders(rollbackRef.current);
            }
            invalidateApi({ key: "api/work-order" });
        },
    });

    const { mutate: deleteWO, isPending: deletingWO } = deleteWorkOrder({
        successMessageFromServer: true,
        errorMessageFromServer: true,
    });

    const bomCode = useMemo(() => {
        if (genericData) {
            return genericData?.item?.filter(
                (it) => it?.id === salesOrder?.product?.id
            )?.[0]?.bom_code;
        }
    }, [genericData, salesOrder]);

    const targetQuantityTolerance = useMemo(() => {
        if (genericData) {
            return Number(
                genericData.typemaster?.find(
                    (post) => post.category_value == "allowance_tolerance"
                )?.value
            );
        }
        return 0;
    }, [genericData]);

    const minTargetQuantityTolerance = useMemo(() => {
        if (genericData) {
            return Number(
                genericData.typemaster?.find(
                    (post) =>
                        post.category_value == "minimum_target_quantity_wo"
                )?.value
            );
        }
        return 0;
    }, [genericData]);

    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { errors },
        watch,
        trigger,
        getValues,
        setValue,
    } = useForm({
        defaultValues: {
            work_orders: [
                {
                    work_order_id: null,
                    target_quantity: null,
                    prod_start_date: null,
                    dispatch_date: null,
                },
            ],
        },
        resolver: zodResolver(
            createSchema(
                targetQuantity ?? 0,
                targetQuantityTolerance ?? 0,
                minTargetQuantityTolerance ?? 0
            )
        ),
        mode: "onBlur",
    });

    const {
        fields: work_orders,
        update: update_work_order,
        insert: insert_work_order,
        append: append_work_order,
        replace: replace_work_orders,
    } = useFieldArray({
        control,
        name: "work_orders",
    });

    const work_order_fields = watch("work_orders");

    const totalProduced =
        work_order_fields?.length > 0
            ? work_order_fields.reduce(
                  (total, work) =>
                      total + (Number(work?.target_quantity_actual) || 0),
                  0
              )
            : 0;

    const getActualQuantity = (quantity) => {
        const required_quantity = multiplicationFactor ?? 1;
        const total_required = Math.ceil(Number(quantity) * required_quantity);
        const total_quantity = Math.round(total_required / required_quantity);

        return total_quantity;
    };

    const getDateForAnIndex = (index, field) => {
        const rawDate = watch(`work_orders.${index}.${field}`);
        if (!rawDate) return undefined;

        const date = new Date(rawDate);
        if (isNaN(date.getTime())) return undefined;

        return date.toISOString().split("T")[0];
    };

    function splitNumber(n, parts = 2) {
        const minPart = Number(targetQuantity) * minTargetQuantityTolerance;
        const result = [];
        const base = Math.floor(n / parts);
        let remainder = n % parts;

        for (let i = 0; i < parts; i++) {
            const part = base + (remainder > 0 ? 1 : 0);
            if (part < minPart) return false;
            result.push(part);
            if (remainder > 0) remainder--;
        }

        return result;
    }

    const localSplit = async (index) => {
        const isValid = await trigger();
        if (!isValid) return;

        const currentWorkOrder = work_order_fields?.[index];

        if (!currentWorkOrder) {
            Swal.fire({
                icon: "error",
                title: "Something went wrong!",
                text: "The work order you are trying to split does not exists",
            });
            return;
        }

        if (isNaN(currentWorkOrder?.target_quantity)) {
            Swal.fire({
                icon: "error",
                title: "Something went wrong!",
                text: "The target quantity of the work order you are trying to split is invalid",
            });
            return;
        }

        const splitedNumbers = splitNumber(
            Number(currentWorkOrder?.target_quantity)
        );

        if (!splitedNumbers) {
            Swal.fire({
                icon: "error",
                title: "Cannot split further!",
                text: `The resulting splitted quantities are less than the allowed threshold i.e ${
                    Number(targetQuantity) * minTargetQuantityTolerance
                }`,
            });
            return;
        }

        rollbackRef.current = getValues("work_orders");

        update_work_order(index, {
            ...currentWorkOrder,
            target_quantity: splitedNumbers?.[0],
            target_quantity_actual: getActualQuantity(splitedNumbers?.[0]),
        });

        insert_work_order(index + 1, {
            ...currentWorkOrder,
            work_order_id: null,
            target_quantity: splitedNumbers?.[1],
            target_quantity_actual: getActualQuantity(splitedNumbers?.[1]),
        });

        const response = {
            sales_order_id: salesOrder?.id,
            created_by: userID,
            updated_by: userID,
            bulk_upload: watch("work_orders")?.map((field) => ({
                id: field?.work_order_id,
                target_quantity: field?.target_quantity_actual,
                prod_start_date: field?.prod_start_date,
                dispatch_date: field?.dispatch_date,
                activity_status: 2,
            })),
        };

        console.log(response);

        mutateCreateWorkOrder({ data: response });
    };

    const globalSplit = async () => {
        const isValid = await trigger();
        if (!isValid) return;

        if (!work_order_fields || work_order_fields?.length === 0) {
            Swal.fire({
                icon: "error",
                title: "Something went wrong!",
                text: "The work orders you are trying to split does not exists",
            });
            return;
        }

        const numberOfSplits = work_order_fields?.length;

        const splitedNumbers = splitNumber(
            Number(targetQuantity),
            numberOfSplits + 1
        );

        if (!splitedNumbers) {
            Swal.fire({
                icon: "error",
                title: "Cannot split further!",
                text: `The resulting splitted quantities are less than the allowed threshold i.e ${
                    Number(targetQuantity) * minTargetQuantityTolerance
                }`,
            });
            return;
        }

        rollbackRef.current = getValues("work_orders");

        work_order_fields?.forEach((work_order, index) => {
            update_work_order(index, {
                ...work_order,
                target_quantity: splitedNumbers?.[index],
                target_quantity_actual: getActualQuantity(
                    splitedNumbers?.[index]
                ),
            });
        });

        append_work_order({
            ...work_order_fields?.[0],
            work_order_id: null,
            target_quantity: splitedNumbers?.[splitedNumbers?.length - 1],
            target_quantity_actual: getActualQuantity(
                splitedNumbers?.[splitedNumbers?.length - 1]
            ),
        });

        const response = {
            sales_order_id: salesOrder?.id,
            created_by: userID,
            updated_by: userID,
            bulk_upload: watch("work_orders")?.map((field) => ({
                id: field?.work_order_id,
                target_quantity: field?.target_quantity_actual,
                prod_start_date: field?.prod_start_date,
                dispatch_date: field?.dispatch_date,
                activity_status: 2,
            })),
        };

        console.log(response);
        mutateCreateWorkOrder({ data: response });
    };

    const handleDeleteWorkOrder = async (work_order_id) => {
        const isValid = await trigger();
        if (!isValid) return;

        Swal.fire({
            text: "Are you sure? You will not be able to recover this data!",
            showDenyButton: true,
            confirmButtonText: "Yes",
            denyButtonText: `No, cancel!`,
        }).then((result) => {
            if (result.isConfirmed) {
                deleteWO({ id: work_order_id });
            }
        });
    };

    const handleSaveWorkOrderByIndex = async (index) => {
        const isValid = await trigger();
        if (!isValid) return;

        const currentWorkOrder = work_order_fields?.[index];

        if (!currentWorkOrder) {
            Swal.fire({
                icon: "error",
                title: "Something went wrong!",
                text: "The work order you are trying to save does not exists",
            });
            return;
        }

        rollbackRef.current = getValues("work_orders");

        const response = {
            sales_order_id: salesOrder?.id,
            created_by: userID,
            updated_by: userID,
            bulk_upload: [
                {
                    id: currentWorkOrder?.work_order_id,
                    target_quantity: currentWorkOrder?.target_quantity_actual,
                    prod_start_date: currentWorkOrder?.prod_start_date,
                    dispatch_date: currentWorkOrder?.dispatch_date,
                    activity_status: 2,
                },
            ],
        };

        console.log(response);

        mutateCreateWorkOrder({ data: response });
    };

    const onSubmit = (data) => {
        const response = {
            sales_order_id: salesOrder?.id,
            created_by: userID,
            updated_by: userID,
            bulk_upload: data?.work_orders?.map((field) => ({
                id: field?.work_order_id,
                target_quantity: field?.target_quantity,
                prod_start_date: field?.prod_start_date,
                dispatch_date: field?.dispatch_date,
                activity_status: 2,
            })),
        };

        rollbackRef.current = getValues("work_orders");

        console.log(response);

        mutateCreateWorkOrder({ data: response });
    };

    useEffect(() => {
        const work_orders = workOrderData?.results?.data || [];
        if (work_orders) {
            reset({
                work_orders: work_orders?.map((wo) => ({
                    work_order_id: wo?.id,
                    work_order_code: wo?.work_order_code,
                    activity_status: wo?.activity_status,
                    target_quantity: wo?.target_quantity,
                    target_quantity_actual: wo?.target_quantity,
                    prod_start_date: moment(wo?.prod_start_date).format(
                        "YYYY-MM-DD"
                    ),
                    dispatch_date: moment(wo?.dispatch_date).format(
                        "YYYY-MM-DD"
                    ),
                    is_bom_generated: wo?.is_bom_generated,
                })),
            });
        }
    }, [workOrderData, workOrderDataUpdatedAt]);

    const is_anything_loading =
        IsGenericLoading ||
        isSalesOrderError ||
        isWorkOrderFetching ||
        isPending ||
        deletingWO;

    if (isSalesOrderLoading) return <>Loading...</>;

    return (
        <div className="card">
            <div className="card-inner">
                <div className="nk-block">
                    <div className="profile-ud-list">
                        <div className="profile-ud-item">
                            <div className="profile-ud wider">
                                <span className="profile-ud-label">
                                    Item Name
                                </span>
                                <span className="profile-ud-value">
                                    {salesOrder?.product?.item_name}
                                </span>
                            </div>
                        </div>
                        <div className="profile-ud-item">
                            <div className="profile-ud wider">
                                <span className="profile-ud-label">
                                    Client Name
                                </span>
                                <span className="profile-ud-value">
                                    {salesOrder?.client?.user?.first_name +
                                        " " +
                                        salesOrder?.client?.user?.last_name}
                                </span>
                            </div>
                        </div>
                        <div className="profile-ud-item">
                            <div className="profile-ud wider">
                                <span className="profile-ud-label">
                                    Sales order
                                </span>
                                <span className="profile-ud-value">
                                    {salesOrder?.so_number}
                                </span>
                            </div>
                        </div>
                        <div className="profile-ud-item">
                            <div className="profile-ud wider">
                                <span className="profile-ud-label">
                                    Product Code
                                </span>
                                <span className="profile-ud-value">
                                    {salesOrder?.product?.item_code}
                                </span>
                            </div>
                        </div>
                        <div className="profile-ud-item">
                            <div className="profile-ud wider">
                                <span className="profile-ud-label">
                                    Total Quantity
                                </span>
                                <span className="profile-ud-value">
                                    {salesOrder?.to_be_produced}
                                </span>
                            </div>
                        </div>
                        <div className="profile-ud-item">
                            <div className="profile-ud wider">
                                <span className="profile-ud-label">
                                    BOM Code
                                </span>
                                <span className="profile-ud-value">
                                    {bomCode}
                                </span>
                            </div>
                        </div>
                        <div className="profile-ud-item">
                            <div className="profile-ud wider">
                                <span className="profile-ud-label">
                                    Location
                                </span>
                                <span className="profile-ud-value">
                                    {salesOrder?.dispatch_location}
                                </span>
                            </div>
                        </div>
                        <div className="profile-ud-item">
                            <div className="profile-ud wider">
                                <span className="profile-ud-label">
                                    Target Date
                                </span>
                                <span className="profile-ud-value">
                                    {moment(salesOrder?.target_date).format(
                                        "DD-MM-YYYY"
                                    )}
                                </span>
                            </div>
                        </div>
                        <div className="profile-ud-item">
                            <div className="profile-ud wider">
                                {salesOrder?.is_stock_allotted ? (
                                    <div className="d-flex">
                                        <span className="profile-ud-label">
                                            Allotted FG
                                        </span>
                                        <div>{salesOrder?.stock_allotted}</div>
                                    </div>
                                ) : (
                                    <div className="d-flex">
                                        <span className="profile-ud-label">
                                            Allotted FG
                                        </span>
                                        <AllotFG
                                            targetQuantity={targetQuantity}
                                            salesOrderId={sales_order_id}
                                            itemId={salesOrder?.product?.id}
                                            disabled={
                                                salesOrder?.activity_status !==
                                                1
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        {salesOrder?.activity_status === 1 &&
                        privileges?.includes("split_wo_action") ? (
                            <div className="profile-ud-item">
                                <div className="profile-ud wider">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                            globalSplit();
                                        }}
                                        disabled={
                                            !salesOrder?.is_stock_allotted ||
                                            is_anything_loading
                                        }
                                    >
                                        <em className="icon ni ni-unlink"></em>
                                        <span>Split All</span>
                                    </button>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
                {salesOrder?.activity_status === 1 ||
                salesOrder?.activity_status === 3 ? (
                    <form
                        className="position-relative"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <div className="nk-block">
                            <div className="profile-ud-list">
                                <div className="profile-ud-item2">
                                    <div className="profile-ud wider">
                                        <span className="profile-ud-label fw-bold text-dark">
                                            Target Quantity
                                        </span>
                                        <span className="profile-ud-value fs-6">
                                            {targetQuantity}
                                        </span>
                                    </div>
                                </div>
                                <div className="profile-ud-item2">
                                    <div className="profile-ud wider">
                                        <span className="profile-ud-label fw-bold text-dark">
                                            To be Produced
                                        </span>
                                        <span className="profile-ud-value fs-6">
                                            {totalProduced}
                                        </span>
                                        {errors?.work_orders
                                            ?.target_quantity && (
                                            <p
                                                style={{
                                                    color: "red",
                                                }}
                                            >
                                                {
                                                    errors?.work_orders
                                                        ?.target_quantity
                                                        ?.message
                                                }
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="profile-ud-item2">
                                    <div className="profile-ud wider">
                                        <span className="profile-ud-label fw-bold text-danger">
                                            Pending Target
                                        </span>
                                        <span className="profile-ud-value fs-6 text-danger">
                                            {Math.max(
                                                targetQuantity - totalProduced,
                                                0
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {work_orders?.map((field, index) => (
                            <div className="card my-2" key={field.id}>
                                <div className="card-inner">
                                    <div className="row g-4">
                                        <div className="col-lg-4 col-md-5 col-sm-12">
                                            <label className="form-label">
                                                Target Quantity
                                                <span className="fs-8 text-danger ms-1">
                                                    *
                                                </span>
                                            </label>
                                            <div className="form-control-wrap">
                                                <div className="d-flex">
                                                    <div>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            id="input-number-number_field"
                                                            placeholder="e.g: Some number..."
                                                            {...register(
                                                                `work_orders.${index}.target_quantity`,
                                                                {
                                                                    valueAsNumber: true,
                                                                    onBlur: () =>
                                                                        trigger(),
                                                                }
                                                            )}
                                                            onChange={(e) => {
                                                                setValue(
                                                                    `work_orders.${index}.target_quantity_actual`,
                                                                    e.target
                                                                        .value
                                                                        ? getActualQuantity(
                                                                              Number(
                                                                                  e
                                                                                      .target
                                                                                      .value
                                                                              )
                                                                          )
                                                                        : ""
                                                                );
                                                            }}
                                                            disabled={
                                                                is_anything_loading ||
                                                                !salesOrder?.is_stock_allotted ||
                                                                !privileges?.includes(
                                                                    "save_wo_action"
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div className="align-self-center text-center mx-1">
                                                        <em className="icon ni ni-arrow-long-right"></em>
                                                    </div>
                                                    <div>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            id="input-number-number_field"
                                                            placeholder="Calculated"
                                                            {...register(
                                                                `work_orders.${index}.target_quantity_actual`,
                                                                {
                                                                    valueAsNumber: true,
                                                                }
                                                            )}
                                                            disabled={
                                                                is_anything_loading ||
                                                                !salesOrder?.is_stock_allotted
                                                            }
                                                            readOnly
                                                        />
                                                    </div>
                                                </div>
                                                {errors.work_orders?.[index]
                                                    ?.target_quantity && (
                                                    <small className="text-danger mt-1">
                                                        {
                                                            errors
                                                                .work_orders?.[
                                                                index
                                                            ]?.target_quantity
                                                                ?.message
                                                        }
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-lg-3 col-md-4 col-sm-12">
                                            <label
                                                className="form-label"
                                                style={{ textWrap: "nowrap" }}
                                            >
                                                Production Start Date
                                                <span className="fs-8 text-danger ms-1">
                                                    *
                                                </span>
                                            </label>
                                            <div className="form-control-wrap">
                                                <Controller
                                                    name={`work_orders.${index}.prod_start_date`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            id="input-date-date"
                                                            max={getDateForAnIndex(
                                                                index,
                                                                "dispatch_date"
                                                            )}
                                                            value={
                                                                field.value instanceof
                                                                Date
                                                                    ? field.value
                                                                          .toISOString()
                                                                          .split(
                                                                              "T"
                                                                          )[0]
                                                                    : field.value ||
                                                                      ""
                                                            }
                                                            onChange={(e) =>
                                                                field.onChange(
                                                                    e.target
                                                                        .value
                                                                        ? new Date(
                                                                              e.target.value
                                                                          )
                                                                        : ""
                                                                )
                                                            }
                                                            onBlur={(e) => {
                                                                field.onBlur();
                                                                trigger();
                                                            }}
                                                            disabled={
                                                                is_anything_loading ||
                                                                !salesOrder?.is_stock_allotted
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.work_orders?.[index]
                                                    ?.prod_start_date && (
                                                    <small className="text-danger mt-1">
                                                        {
                                                            errors
                                                                .work_orders?.[
                                                                index
                                                            ]?.prod_start_date
                                                                ?.message
                                                        }
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-lg-2 col-md-4 col-sm-12">
                                            <label className="form-label">
                                                Dispatch Date
                                                <span className="fs-8 text-danger ms-1">
                                                    *
                                                </span>
                                            </label>
                                            <div className="form-control-wrap">
                                                <Controller
                                                    name={`work_orders.${index}.dispatch_date`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            id="input-date-date"
                                                            min={getDateForAnIndex(
                                                                index,
                                                                "prod_start_date"
                                                            )}
                                                            max={
                                                                salesOrder?.target_date
                                                                    ? new Date(
                                                                          salesOrder?.target_date
                                                                      )
                                                                          .toISOString()
                                                                          .split(
                                                                              "T"
                                                                          )[0]
                                                                    : undefined
                                                            }
                                                            value={
                                                                field.value instanceof
                                                                Date
                                                                    ? field.value
                                                                          .toISOString()
                                                                          .split(
                                                                              "T"
                                                                          )[0]
                                                                    : field.value ||
                                                                      ""
                                                            }
                                                            onChange={(e) =>
                                                                field.onChange(
                                                                    e.target
                                                                        .value
                                                                        ? new Date(
                                                                              e.target.value
                                                                          )
                                                                        : ""
                                                                )
                                                            }
                                                            onBlur={(e) => {
                                                                field.onBlur();
                                                                trigger();
                                                            }}
                                                            disabled={
                                                                is_anything_loading ||
                                                                !salesOrder?.is_stock_allotted
                                                            }
                                                        />
                                                    )}
                                                />
                                                {errors.work_orders?.[index]
                                                    ?.dispatch_date && (
                                                    <small className="text-danger mt-1">
                                                        {
                                                            errors
                                                                .work_orders?.[
                                                                index
                                                            ]?.dispatch_date
                                                                ?.message
                                                        }
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                        {privileges?.includes(
                                            "split_wo_action"
                                        ) && (
                                            <div className="col-1 col-md-1 col-sm-12 mb-1 d-flex align-items-end justify-content-start">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-success"
                                                    onClick={() => {
                                                        localSplit(index);
                                                    }}
                                                    disabled={
                                                        !salesOrder?.is_stock_allotted ||
                                                        is_anything_loading
                                                    }
                                                >
                                                    <em className="icon ni ni-unlink"></em>
                                                </button>
                                            </div>
                                        )}
                                        {privileges?.includes(
                                            "save_wo_action"
                                        ) && (
                                            <div className="col-lg-1 col-md-3 col-sm-12 mb-1 d-flex align-items-end justify-content-start">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-info"
                                                    onClick={() => {
                                                        handleSaveWorkOrderByIndex(
                                                            index
                                                        );
                                                    }}
                                                    disabled={
                                                        !salesOrder?.is_stock_allotted ||
                                                        is_anything_loading
                                                    }
                                                >
                                                    <em className="icon ni ni-save-fill"></em>
                                                </button>
                                            </div>
                                        )}
                                        {work_orders?.length > 1 &&
                                            privileges?.includes(
                                                "delete_wo_action"
                                            ) && (
                                                <div className="col-lg-1 col-md-3 col-sm-12 mb-1 d-flex align-items-end justify-content-start">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => {
                                                            handleDeleteWorkOrder(
                                                                field?.work_order_id
                                                            );
                                                        }}
                                                        disabled={
                                                            !salesOrder?.is_stock_allotted ||
                                                            is_anything_loading
                                                        }
                                                    >
                                                        <em className="icon ni ni-trash-fill"></em>
                                                    </button>
                                                </div>
                                            )}
                                        {field?.work_order_id && (
                                            <div className="col-lg-12 col-md-12 col-sm-12">
                                                <div className="nk-block-head nk-block-head-sm">
                                                    <div className="nk-block-head-content">
                                                        <div className="d-flex align-items-baseline">
                                                            <h6
                                                                className={`h7 pe-3 text-${
                                                                    field?.is_bom_generated
                                                                        ? "success"
                                                                        : "danger"
                                                                }`}
                                                                title={`${
                                                                    field?.is_bom_generated
                                                                        ? "BOM Generated"
                                                                        : "BOM Not Generated/Updated"
                                                                }`}
                                                            >
                                                                {
                                                                    field.work_order_code
                                                                }
                                                            </h6>
                                                            {privileges?.includes(
                                                                "generate_bom"
                                                            ) && (
                                                                <CheckBOM
                                                                    item_id={
                                                                        salesOrder
                                                                            ?.product
                                                                            ?.id
                                                                    }
                                                                    wo_id={
                                                                        field.work_order_id
                                                                    }
                                                                    salesorder_id={
                                                                        salesOrder?.id
                                                                    }
                                                                    disabled={
                                                                        !salesOrder?.is_stock_allotted ||
                                                                        is_anything_loading
                                                                    }
                                                                    item_name={
                                                                        salesOrder
                                                                            ?.product
                                                                            ?.item_name
                                                                    }
                                                                    work_order_code={
                                                                        field.work_order_code
                                                                    }
                                                                    sales_order_no={
                                                                        salesOrder?.so_number
                                                                    }
                                                                    setCurrentWIP={
                                                                        setCurrentWIP
                                                                    }
                                                                    recheckBom={
                                                                        recheckBom
                                                                    }
                                                                    setRecheckBom={
                                                                        setRecheckBom
                                                                    }
                                                                />
                                                            )}
                                                            <GeneratedBOMDetails
                                                                wo_id={
                                                                    field.work_order_id
                                                                }
                                                                item_name={
                                                                    salesOrder
                                                                        ?.product
                                                                        ?.item_name
                                                                }
                                                                work_order_code={
                                                                    field.work_order_code
                                                                }
                                                                sales_order_no={
                                                                    salesOrder?.so_number
                                                                }
                                                                disabled={
                                                                    !salesOrder?.is_stock_allotted ||
                                                                    is_anything_loading
                                                                }
                                                            />
                                                            <AllotWIP
                                                                targetQuantity={
                                                                    currentWIP?.work_order_req_quantity -
                                                                    currentWIP?.allotted_stock
                                                                }
                                                                wipId={
                                                                    currentWIP?.id
                                                                }
                                                                itemId={
                                                                    currentWIP?.item_id
                                                                }
                                                                salesorderId={
                                                                    salesOrder?.id
                                                                }
                                                                workOrderId={
                                                                    currentWIP?.wo_id
                                                                }
                                                                setRecheckBom={
                                                                    setRecheckBom
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="card my-2">
                            <div className="card-inner">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary me-2"
                                    onClick={() => navigate(-1)}
                                >
                                    Back
                                </button>
                                {privileges?.includes("save_wo_action") && (
                                    <button
                                        className="btn btn-sm btn-primary"
                                        disabled={
                                            is_anything_loading ||
                                            !salesOrder?.is_stock_allotted
                                        }
                                    >
                                        Save All
                                    </button>
                                )}
                            </div>
                        </div>
                        {is_anything_loading && (
                            <div
                                className="position-absolute top-0 left-0 h-100 w-100 z-1 d-flex align-items-center justify-content-center"
                                style={{
                                    backgroundColor: "rgb(255, 255, 255, 0.5)",
                                }}
                            >
                                <div
                                    className="spinner-border text-primary ms-2"
                                    role="status"
                                >
                                    <span className="sr-only"></span>
                                </div>
                            </div>
                        )}
                    </form>
                ) : null}
            </div>
        </div>
    );
};

export default WorkOrderSplitUpdate;
